import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faArrowUpRightFromSquare, faCode, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { Avatar, Badge, Button, Checkbox, KebabMenu, SegmentedControl, useToast, type BadgeTone } from '../ui';
import type { Me } from './useFeedbackFeed';
import {
  answerNote, deleteNote, fetchMessages, replyToNote, setNoteStatus, shotUrl,
  KIND_LABEL, STATUS_LABEL, STATUS_TONE, type FeedbackItem, type FeedbackMessage,
} from '../../lib/clientFeedback';
import { timeAgo } from '../../lib/comms';
import Icon from '../ui/Icon';

/* One note in the feedback panel: what the client said, where, the pinned
   screenshot (click for the whole screen with the pin), the conversation with
   the client, and the actions. Technical details (element, errors) stay
   folded away until someone needs them. */

const KIND_TONE: Record<FeedbackItem['kind'], BadgeTone> = { bug: 'down', change: 'neutral', question: 'info' };

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Small copy-to-clipboard icon: a check for a moment once copied. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="hover-wash pressable"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(value).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1400);
        }).catch(() => undefined);
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
        border: 'none', borderRadius: 'var(--radius-xs)', background: 'transparent', cursor: 'pointer',
        color: done ? 'var(--status-up)' : 'var(--text-faint)',
      }}
    >
      <Icon name={done ? 'check' : 'copy'} size={12} />
    </button>
  );
}

/** A quiet "▸ Label" toggle that reveals its children. */
export function Disclosure({ label, open, onToggle, children, id }: {
  label: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  id: string;
}) {
  return (
    <div>
      <button
        type="button"
        className="ck-fb-disclosure hover-wash"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 6px', margin: '0 -6px',
          border: 'none', borderRadius: 'var(--radius-xs)', background: 'transparent', cursor: 'pointer',
          fontFamily: 'var(--font-ui)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)',
        }}
      >
        <Icon name="chevron-right" size={12} className="ck-fb-chevron" />
        {label}
      </button>
      {open ? <div id={id} className="ck-fb-reveal" style={{ marginTop: 8 }}>{children}</div> : null}
    </div>
  );
}

/** A coloured strip for things someone has to act on: withdrawn notes, a job with nothing left in it. */
export function Banner({ tone, icon = 'triangle-alert', title, children, action }: {
  tone: 'down' | 'warn' | 'info' | 'up';
  icon?: string;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      role={tone === 'down' ? 'alert' : 'status'}
      style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
        background: `var(--status-${tone}-soft)`,
        border: `1px solid color-mix(in srgb, var(--status-${tone}) 28%, transparent)`,
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)', lineHeight: 1.5, color: 'var(--text-body)',
      }}
    >
      <Icon name={icon} size={15} style={{ color: `var(--status-${tone})`, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title ? <div style={{ fontWeight: 600, color: `var(--status-${tone})` }}>{title}</div> : null}
        {children}
      </div>
      {action ? <div style={{ flexShrink: 0, alignSelf: 'center' }}>{action}</div> : null}
    </div>
  );
}

/** The whole screen as the client saw it, with their pin, over everything. Esc or a click outside closes it. */
function Lightbox({ src, alt, pin, title, onClose }: {
  src: string;
  alt: string;
  pin: { x: number; y: number; n: number } | null;
  title: string;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    // Capture, and stop it there: Esc closes the picture, not the side panel under it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return createPortal(
    <div
      className="ck-fb-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 120, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12, padding: '56px 24px 32px',
        background: 'rgba(12, 10, 10, 0.82)', backdropFilter: 'blur(3px)',
      }}
    >
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', color: '#F5F2EF' }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <a href={src} target="_blank" rel="noreferrer" className="hover-wash pressable" style={lightboxBtn}>
            <Icon name="external-link" size={14} /> Open original
          </a>
          <button ref={closeRef} type="button" className="hover-wash pressable" style={lightboxBtn} onClick={onClose} aria-label="Close screenshot">
            <Icon name="x" size={14} /> Close
          </button>
        </span>
      </div>
      <div className="ck-fb-lightbox-img" style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} style={{ maxWidth: 'min(1400px, calc(100vw - 48px))', maxHeight: 'calc(100vh - 110px)', borderRadius: 'var(--radius-md)', boxShadow: '0 24px 80px rgba(0,0,0,0.55)' }} />
        {pin ? (
          <span
            aria-label={`Pin ${pin.n}`}
            style={{
              position: 'absolute', left: `${pin.x}%`, top: `${pin.y}%`, transform: 'translate(-50%, -50%)',
              width: 26, height: 26, borderRadius: 'var(--radius-pill)', background: 'var(--accent)', color: 'var(--text-on-accent)',
              border: '2px solid #fff', boxShadow: '0 0 0 4px rgba(217, 86, 54, 0.35), 0 4px 12px rgba(0,0,0,0.4)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
            }}
          >
            {pin.n}
          </span>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

const lightboxBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 10px', borderRadius: 'var(--radius-sm)',
  border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.06)', color: '#F5F2EF',
  fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', cursor: 'pointer', textDecoration: 'none',
};

/** One line of the conversation on a note. Bullfinch on the right in our colour, the client on the left. */
function Bubble({ fromUs, who, at, badge, children }: {
  fromUs: boolean;
  who: string;
  at?: string | null;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexDirection: fromUs ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
      <Avatar name={who} size={24} style={fromUs ? { flexShrink: 0 } : { flexShrink: 0, background: 'var(--surface-active)', color: 'var(--text-muted)' }} />
      <div style={{ maxWidth: '82%', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: fromUs ? 'flex-end' : 'flex-start', marginBottom: 3, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{who}</span>
          {badge}
          {at ? <span>· {timeAgo(at)}</span> : null}
        </div>
        <div style={{
          padding: '8px 12px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 'var(--text-sm)', lineHeight: 1.5, color: 'var(--text-body)',
          background: fromUs ? 'var(--accent-soft)' : 'var(--surface-raised)',
          border: `1px solid ${fromUs ? 'color-mix(in srgb, var(--accent) 22%, transparent)' : 'var(--border-hairline)'}`,
          borderRadius: fromUs ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export interface NotePick {
  checked: boolean;
  disabled: boolean;
  reason: string | null;
  onChange: (on: boolean) => void;
}

/** One note in the panel as a card: what, where, screenshot, conversation, actions. */
export function FeedbackNoteCard({ item, index, me, sender, onChanged, pick }: {
  item: FeedbackItem;
  index: number;
  me: Me;
  /** Who sent the batch, for the conversation and the delete confirm. */
  sender: string;
  onChanged: () => Promise<void> | void;
  /** Present while "Select to merge" is on and this note can be picked. */
  pick?: NotePick;
}) {
  const toast = useToast();
  const [crop, setCrop] = useState<string | null>(null);
  const [full, setFull] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const [thread, setThread] = useState<FeedbackMessage[]>([]);
  const [tech, setTech] = useState(false);
  const [composing, setComposing] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const unanswered = item.kind === 'question' && !item.answer;
  const [mode, setMode] = useState<'reply' | 'answer'>(unanswered ? 'answer' : 'reply');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { void shotUrl(item.crop_path).then(setCrop); }, [item.crop_path]);
  useEffect(() => { void fetchMessages(item.id).then(setThread).catch(() => undefined); }, [item.id]);
  useEffect(() => {
    if (zoom && !full) void shotUrl(item.screenshot_path).then(setFull);
  }, [zoom, full, item.screenshot_path]);

  const withdrawn = item.status === 'withdrawn';
  const where = item.page_title?.replace(/\s*[|–-].*$/, '') || item.route || item.page_url || 'Unknown page';
  const errors = [
    ...(item.errors ?? []).map((e) => ({ at: e.at, message: e.message, kind: 'Console' })),
    ...(item.network ?? []).map((n) => ({ at: n.at, message: `${n.method} ${n.url} → ${n.status}${n.detail ? ` ${n.detail}` : ''}`, kind: 'Network' })),
  ];
  const hasTech = !!(item.element?.selector || item.element?.nearby?.length || errors.length || item.app_version || item.click?.xPct != null);
  const expanded = composing || !!text;
  const showComposer = !withdrawn && (composing || thread.length > 0 || !!text);
  const canClose = item.status !== 'closed' && !withdrawn;

  const startReply = (as: 'reply' | 'answer') => {
    setMode(as);
    setComposing(true);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
  };

  const submit = async () => {
    if (!me || !text.trim() || sending) return;
    setSending(true);
    try {
      if (mode === 'answer') await answerNote(item.id, text);
      else await replyToNote(item.id, me, text);
      setText('');
      setComposing(false);
      setMode('reply');
      setThread(await fetchMessages(item.id));
      await onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  const closeNote = async () => {
    setClosing(true);
    try { await setNoteStatus(item.id, 'closed', 'Closed by Bullfinch'); await onChanged(); }
    catch (err) { toast.error((err as Error).message); }
    finally { setClosing(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try { await deleteNote(item.id); await onChanged(); }
    catch (err) { toast.error((err as Error).message); setDeleting(false); }
  };

  const pinNo = index + 1;
  const pin = item.click?.xPct != null && item.click?.yPct != null && full ? { x: item.click.xPct, y: item.click.yPct, n: pinNo } : null;

  return (
    <article
      aria-label={`Note ${pinNo}`}
      style={{
        position: 'relative',
        background: 'var(--surface-card)',
        border: `1px solid ${pick?.checked ? 'color-mix(in srgb, var(--accent) 55%, var(--border-hairline))' : 'var(--border-hairline)'}`,
        boxShadow: pick?.checked ? '0 0 0 3px var(--accent-soft)' : 'var(--shadow-rest)',
        borderRadius: 'var(--radius-lg)',
        transition: 'border-color var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out)',
      }}
    >
      <div style={{ padding: '14px 16px 0', opacity: withdrawn ? 0.6 : undefined }}>
        {/* What and where */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {pick ? (
            <span title={pick.reason ?? 'Pick this note to merge'} className="ck-fb-reveal" style={{ display: 'inline-flex' }}>
              <Checkbox checked={pick.checked} disabled={pick.disabled} onChange={pick.onChange} />
            </span>
          ) : null}
          <span
            aria-hidden="true"
            style={{
              width: 22, height: 22, borderRadius: 'var(--radius-pill)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: withdrawn ? 'var(--surface-active)' : 'var(--accent)', color: withdrawn ? 'var(--text-muted)' : 'var(--text-on-accent)',
              fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, flexShrink: 0,
            }}
          >
            {pinNo}
          </span>
          <Badge tone={KIND_TONE[item.kind]}>{KIND_LABEL[item.kind]}</Badge>
          <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
          <span
            title={[item.page_title, item.route ?? item.page_url].filter(Boolean).join('\n')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}
          >
            <Icon name={item.origin === 'website' ? 'globe' : 'file-text'} size={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{where}</span>
            {item.route && item.page_title ? <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-faint)' }}>{item.route}</span> : null}
          </span>
          {item.pinned_at ? <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{timeAgo(item.pinned_at)}</span> : null}
        </div>

        {/* The message, and the pinned spot */}
        <div className={crop ? 'ck-fb-body ck-fb-body--shot' : 'ck-fb-body'}>
          <div style={{ minWidth: 0 }}>
            <p style={{
              margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 'var(--text-md)', lineHeight: 1.55, color: 'var(--text-body)',
              textDecoration: withdrawn ? 'line-through' : undefined, textDecorationColor: 'var(--text-faint)',
            }}>
              {item.message}
            </p>
            {(item.record?.type || item.record?.refs?.length || errors.length) ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {item.record?.type ? (
                  <Badge tone="neutral" title={item.record.id ?? undefined}>{item.record.type}{item.record.id ? ` · ${item.record.id.slice(0, 8)}` : ''}</Badge>
                ) : null}
                {item.record?.refs?.length ? <Badge tone="neutral" title="On screen when pinned">{item.record.refs.join(', ')}</Badge> : null}
                {errors.length ? (
                  <button type="button" onClick={() => setTech(true)} style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer' }} title="Show the errors">
                    <Badge tone="warn" dot>{errors.length} error{errors.length === 1 ? '' : 's'}</Badge>
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          {crop ? (
            <button
              type="button"
              className="ck-fb-shot pressable"
              onClick={() => setZoom(true)}
              aria-label={`Open the full screenshot for note ${pinNo}`}
              style={{
                position: 'relative', padding: 0, border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)',
                overflow: 'hidden', background: 'var(--surface-raised)', cursor: 'zoom-in', lineHeight: 0,
              }}
            >
              <img src={crop} alt={`Where note ${pinNo} was pinned`} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: withdrawn ? 'grayscale(1)' : undefined }} />
              <span className="ck-fb-shot-hint" style={{
                position: 'absolute', inset: 'auto 6px 6px auto', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px',
                borderRadius: 'var(--radius-pill)', background: 'rgba(20, 16, 14, 0.72)', color: '#fff', fontSize: 11, lineHeight: 1.4, fontFamily: 'var(--font-ui)',
              }}>
                <Icon name="search" size={11} /> Full screen
              </span>
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ padding: '12px 16px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {withdrawn ? (
          <Banner tone="down" title={`${sender} withdrew this note${item.withdrawn_at ? ` ${timeAgo(item.withdrawn_at)}` : ''}`}>
            Stop any work on it. It stays here so whoever has it knows.
          </Banner>
        ) : null}

        {hasTech ? (
          <Disclosure id={`fb-tech-${item.id}`} open={tech} onToggle={() => setTech((o) => !o)} label={<>Technical details{errors.length ? <span style={{ color: 'var(--status-warn)' }}> · {errors.length} error{errors.length === 1 ? '' : 's'}</span> : null}</>}>
            <dl style={{
              margin: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '6px 14px', padding: '10px 12px',
              background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)',
            }}>
              {item.element?.selector ? (
                <>
                  <dt style={techLabel}>Pinned on</dt>
                  <dd style={techValue}>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>&lt;{item.element.tag}&gt;{item.element.text ? ` “${item.element.text}”` : ''}<br /><span style={{ color: 'var(--text-faint)' }}>{item.element.selector}</span></span>
                      <CopyButton value={item.element.selector} label="selector" />
                    </span>
                  </dd>
                </>
              ) : null}
              {item.element?.heading ? (<><dt style={techLabel}>Under heading</dt><dd style={techValue}>{item.element.heading}</dd></>) : null}
              {item.element?.nearby?.length ? (<><dt style={techLabel}>Nearby text</dt><dd style={techValue}>{item.element.nearby.join(' · ')}</dd></>) : null}
              {item.click?.xPct != null ? (
                <><dt style={techLabel}>Pin</dt><dd style={techValue}>{item.click.xPct}% across, {item.click.yPct}% down{item.click.viewport ? ` · ${item.click.viewport.w}×${item.click.viewport.h} screen` : ''}</dd></>
              ) : null}
              {item.app_version ? (<><dt style={techLabel}>App version</dt><dd style={techValue}>{item.app_version}</dd></>) : null}
              {errors.length ? (
                <>
                  <dt style={techLabel}>Errors</dt>
                  <dd style={{ ...techValue, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {errors.slice(-6).map((e, i) => (
                      <span key={i} style={{ color: 'var(--status-warn)' }}>
                        <span style={{ color: 'var(--text-faint)' }}>{e.kind} · </span>{e.message}
                      </span>
                    ))}
                  </dd>
                </>
              ) : null}
            </dl>
          </Disclosure>
        ) : null}

        {/* The conversation with the client on this note */}
        {(item.answer || thread.length || showComposer) ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 12, borderTop: '1px solid var(--border-hairline)' }}>
            {item.answer ? (
              <Bubble fromUs who="Bullfinch" badge={<Badge tone="up">Answer</Badge>}>{item.answer}</Bubble>
            ) : null}
            {thread.map((m) => (
              <Bubble key={m.id} fromUs={m.from_bullfinch} who={m.author_name ?? (m.from_bullfinch ? 'Bullfinch' : sender)} at={m.created_at}>{m.body}</Bubble>
            ))}
            {showComposer ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  ref={inputRef}
                  className="ck-textarea"
                  value={text}
                  rows={expanded ? 3 : 1}
                  aria-label={mode === 'answer' ? `Answer ${sender}` : `Reply to ${sender}`}
                  placeholder={mode === 'answer' ? `Answer ${sender}’s question…` : `Reply to ${sender}…`}
                  onFocus={() => setComposing(true)}
                  onBlur={() => { if (!text.trim()) setComposing(false); }}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void submit(); }
                    if (e.key === 'Escape') { e.stopPropagation(); setText(''); setComposing(false); e.currentTarget.blur(); }
                  }}
                  style={{ resize: expanded ? 'vertical' : 'none', minHeight: expanded ? 84 : 38, transition: 'min-height var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)' }}
                />
                {expanded ? (
                  <div className="ck-fb-reveal" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {item.kind === 'question' ? (
                      <SegmentedControl
                        options={[{ id: 'answer', label: 'Answer' }, { id: 'reply', label: 'Reply' }]}
                        value={mode}
                        onChange={(v: string) => setMode(v as 'reply' | 'answer')}
                      />
                    ) : null}
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                      {mode === 'answer' ? 'Marks the question answered in their app' : 'Shows on the note in their app'} · {isMac ? '⌘' : 'Ctrl'}+Enter
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <Button size="sm" variant="ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => { setText(''); setComposing(false); }}>Cancel</Button>
                      <Button size="sm" icon="send" disabled={!text.trim() || !me || sending} onMouseDown={(e) => e.preventDefault()} onClick={() => void submit()}>
                        {sending ? 'Sending…' : mode === 'answer' ? 'Send answer' : 'Send'}
                      </Button>
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Actions */}
      {confirmDelete ? (
        <div className="ck-fb-reveal" role="alertdialog" aria-label="Delete this note?" style={{
          margin: '12px 0 0', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          background: 'var(--status-down-soft)', borderTop: '1px solid color-mix(in srgb, var(--status-down) 25%, transparent)',
          borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', fontSize: 'var(--text-sm)', color: 'var(--text-body)',
        }}>
          <Icon name="trash-2" size={14} style={{ color: 'var(--status-down)' }} />
          <span style={{ flex: 1, minWidth: 200 }}>Delete this note here and in {sender}’s app? This can’t be undone.</span>
          <Button size="sm" variant="ghost" disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button size="sm" variant="danger" icon="trash-2" disabled={deleting} onClick={() => void remove()}>{deleting ? 'Deleting…' : 'Delete note'}</Button>
        </div>
      ) : (
        <div style={{ padding: '12px 16px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          {!withdrawn && !expanded ? (
            <Button size="sm" variant={unanswered || (!thread.length && !item.answer) ? 'primary' : 'secondary'} icon={unanswered ? 'help-circle' : 'message-square'} disabled={!me} onClick={() => startReply(unanswered ? 'answer' : 'reply')}>
              {unanswered ? 'Answer' : 'Reply'}
            </Button>
          ) : null}
          {canClose ? (
            <Button size="sm" variant="secondary" icon="check" disabled={closing} onClick={() => void closeNote()} title="Close the note in their app with no change made">
              {closing ? 'Closing…' : 'Close without a change'}
            </Button>
          ) : null}
          <span style={{ marginLeft: 'auto' }}>
            <KebabMenu
              label={`More for note ${pinNo}`}
              actions={[
                { label: 'Open page', icon: faArrowUpRightFromSquare, disabled: !item.page_url, onClick: () => { if (item.page_url) window.open(item.page_url, '_blank', 'noopener'); } },
                {
                  label: 'Copy selector', icon: faCode, disabled: !item.element?.selector,
                  onClick: () => {
                    void navigator.clipboard.writeText(item.element?.selector ?? '').then(() => toast.success('Selector copied')).catch(() => undefined);
                  },
                },
                { label: 'Delete note…', icon: faTrashCan, danger: true, onClick: () => setConfirmDelete(true) },
              ]}
            />
          </span>
        </div>
      )}

      {zoom && (full || crop) ? (
        <Lightbox
          src={(full ?? crop) as string}
          alt={`The screen when note ${pinNo} was pinned`}
          pin={pin}
          title={`Note ${pinNo} · ${where}`}
          onClose={() => setZoom(false)}
        />
      ) : null}
    </article>
  );
}

const techLabel: React.CSSProperties = { margin: 0, color: 'var(--text-faint)', whiteSpace: 'nowrap' };
const techValue: React.CSSProperties = { margin: 0, fontFamily: 'var(--font-mono)', color: 'var(--text-body)', overflowWrap: 'anywhere', lineHeight: 1.5 };

